// ─── 🌙 La caparra, nella scheda della prenotazione ──────────────────────────
//
// Dal mockup approvato il 25/09/2026 (sezioni 7 e 10). Com'è la caparra, e i
// gesti della Segreteria:
//   · 📞 «Disdetta per telefono» — vale l'ora in cui la si registra (R10);
//   · 🚫 «Segna no-show» — solo a partita cominciata;
//   · 💶 «Accredita a discrezione» — solo su una caparra trattenuta, col motivo;
//   · ↩️ «Annulla il no-show» (voce 30, strada A) — la caparra trattenuta per un
//     no-show sbagliato torna da scalare, col perché;
//   · 💶 «Scala la caparra» — prima la quota di chi ha organizzato, il resto in
//     parti uguali dagli altri (scelta A). A fine partita lo fa da sé.
//
// 🆕 Fase 3 (26/09/2026, mockup approvato):
//   · 💶 «Incassata qui» su una caparra che aspetta: contanti, 📟 il lettore
//     SumUp Solo (il gestionale gli manda l'importo e sa da solo com'è andata),
//     o la carta segnata a mano (R9);
//   · la caparra del CAMPO FISSO, vista da una sua partita: una sola per tutte,
//     si scala dall'ultima; e «🌙 Chiedi l’acconto del campo fisso» sui fissi
//     serali nati prima (R7).

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  comeEFinita,
  formatCents,
  type CaparraDellaSegreteria,
  type CaparraDelFissoScelta,
  type SchedaPrenotazione,
  type StatoDelLettore,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { giornoBreve } from '../../lib/time.js';
import { Guasto } from '../../components/pezzi.js';

const COME: Record<string, string> = {
  card: 'con la carta, online (SumUp)',
  credit: 'coi Crediti del borsellino',
  mixed: 'in parte coi Crediti, in parte con la carta',
  desk_cash: 'al banco, in contanti',
  desk_card: 'al banco, con la carta',
};

const PERCHE: Record<string, string> = {
  tempo_scaduto: 'il tempo per pagare è scaduto',
  annullata: 'annullata prima di pagare',
  disdetta_in_tempo: 'disdetta in tempo',
  disdetta_tardiva: 'disdetta dentro le ore della disdetta',
  circolo: 'annullata dal circolo',
  no_show: 'non si sono presentati',
  giocata: 'partita giocata',
  pagamento_tardivo: 'pagato dopo il tempo, e il campo era stato preso',
};

export function etichettaCaparra(c: { status: string }): { testo: string; classe: string } {
  switch (c.status) {
    case 'pending':
      return { testo: '⏳ Attende', classe: 'pill-attesa' };
    case 'paid':
      return { testo: '💳 Pagato', classe: 'pill-ok' };
    case 'consumed':
      return { testo: '✓ Scalato', classe: 'pill-ok' };
    case 'forfeited':
      return { testo: '✖ Trattenuto', classe: 'pill-persa' };
    case 'credited':
      return { testo: '💶 Nel borsellino', classe: 'pill-credito' };
    default:
      return { testo: 'Non pagato', classe: 'pill-credito' };
  }
}

const perche = (r: string | null) =>
  r === null
    ? null
    : r.startsWith('a_discrezione:')
      ? `a discrezione: ${r.slice(14).trim()}`
      : (PERCHE[r] ?? comeEFinita(r).toLowerCase());

type EsitoDelLettore = {
  outcome: 'attende' | 'pagata' | 'rifiutata' | 'annullata';
  deposit: CaparraDellaSegreteria;
};

export function CaparraDellaScheda({
  p,
  puoModificare,
  puoIncassare,
}: {
  p: SchedaPrenotazione;
  puoModificare: boolean;
  puoIncassare: boolean;
}): React.JSX.Element | null {
  const c = p.caparraDettaglio;
  const qc = useQueryClient();
  const [apre, setApre] = useState<'telefono' | 'noshow' | 'credito' | 'annulla' | 'banco' | null>(null);
  const [motivo, setMotivo] = useState('');
  const [esitoLettore, setEsitoLettore] = useState<EsitoDelLettore['outcome'] | null>(null);
  const fisso = c?.seriesId ?? null;
  const inAttesa = c?.status === 'pending';
  const lettore = useQuery({
    queryKey: ['stato-lettore'],
    queryFn: () => chiama<StatoDelLettore>('GET', '/deposits/reader-status'),
    enabled: inAttesa,
    staleTime: 60_000,
  });
  const rileggi = () => {
    void qc.invalidateQueries({ queryKey: ['prenotazione', p.id] });
    void qc.invalidateQueries({ queryKey: ['calendario'] });
    void qc.invalidateQueries({ queryKey: ['organizzatore-partita', p.id] });
  };
  const gesto = useMutation({
    meta: {
      fatto: (_d, g) =>
        ({
          telefono: 'disdetta per telefono registrata',
          noshow: 'no-show segnato: l’acconto resta al circolo',
          credito: 'acconto messo nel borsellino',
          scala: 'acconto scalato dal totale',
          annulla: 'no-show annullato',
        })[g as 'telefono' | 'noshow' | 'credito' | 'scala' | 'annulla'],
    },
    mutationFn: (g: 'telefono' | 'noshow' | 'credito' | 'scala' | 'annulla') =>
      g === 'telefono'
        ? chiama<CaparraDellaSegreteria | null>('POST', `/bookings/${p.id}/phone-cancel`)
        : g === 'noshow'
          ? // 🆕 Fase 3 (R7): sul campo fisso il no-show è di QUESTA partita, e la caparra è del fisso.
            fisso
            ? chiama('POST', `/bookings/${p.id}/no-show`)
            : chiama<CaparraDellaSegreteria>('POST', `/deposits/${c!.id}/no-show`)
          : g === 'credito'
            ? chiama<CaparraDellaSegreteria>('POST', `/deposits/${c!.id}/credit`, { reason: motivo.trim() })
            : g === 'annulla'
              ? chiama('POST', `/bookings/${p.id}/no-show/cancel`, { reason: motivo.trim() })
              : chiama<CaparraDellaSegreteria>('POST', `/deposits/${c!.id}/apply`),
    onSuccess: () => {
      setApre(null);
      setMotivo('');
      rileggi();
    },
  });

  // 💶 «Incassata qui»: contanti o carta segnata a mano.
  const banco = useMutation({
    meta: {
      fatto: (_d, m) =>
        m === 'cash'
          ? 'acconto incassato in contanti · link ritirato'
          : 'acconto incassato con la carta · link ritirato',
    },
    mutationFn: (m: 'cash' | 'card') =>
      chiama<CaparraDellaSegreteria>('POST', `/deposits/${c!.id}/desk`, { method: m }),
    onSuccess: () => {
      setApre(null);
      rileggi();
    },
  });
  // 📟 Il lettore Solo: manda, poi rilegge ogni due secondi finché non risponde.
  const manda = useMutation({
    meta: { fatto: () => null },
    mutationFn: () => chiama<EsitoDelLettore>('POST', `/deposits/${c!.id}/reader`),
    onSuccess: (e) => {
      setEsitoLettore(e.outcome);
      rileggi();
    },
  });
  const ferma = useMutation({
    meta: { fatto: 'pagamento annullato sul lettore' },
    mutationFn: () => chiama<EsitoDelLettore>('POST', `/deposits/${c!.id}/reader/cancel`),
    onSuccess: (e) => {
      setEsitoLettore(e.outcome);
      rileggi();
    },
  });
  const sulLettore = c?.onReader === true;
  useEffect(() => {
    if (!sulLettore || !c) return;
    const t = setInterval(() => {
      void chiama<EsitoDelLettore>('GET', `/deposits/${c.id}/reader`)
        .then((e) => {
          if (e.outcome !== 'attende') {
            setEsitoLettore(e.outcome);
            rileggi();
          }
        })
        .catch(() => undefined);
    }, 2000);
    return () => clearInterval(t);
  }, [sulLettore, c?.id]);

  if (!c) {
    return p.caparraDelFissoDaChiedere ? (
      <ChiediLaCaparraDelFisso p={p} puoModificare={puoModificare} rileggi={rileggi} />
    ) : null;
  }
  const e = etichettaCaparra(c);
  const viva = p.status === 'confirmed';
  const oreFino = Math.max(0, Math.round((Date.parse(p.startsAt) - Date.now()) / 3_600_000));

  return (
    <div className="caparra-scheda">
      <h3 className="titolo-sezione">
        {fisso ? 'Acconto del campo fisso' : 'Acconto'}{' '}
        <span className={`pill-caparra ${e.classe}`}>{e.testo}</span>
      </h3>
      {fisso && (
        <p className="tenue piccolo">
          Uno solo per tutte le partite del campo fisso: si scala dall’ultima. Se una sera il campo resta
          vuoto, o la partita si disdice a meno di 48 ore, resta al circolo e ne serve uno nuovo entro 48 ore
          dalla partita dopo, altrimenti le partite successive si liberano.
        </p>
      )}
      <dl>
        <dt>Importo</dt>
        <dd>
          {formatCents(c.amountCents)} · di {c.memberName}
        </dd>
        {c.paidWith && (
          <>
            <dt>Pagato</dt>
            <dd>
              {COME[c.paidWith] ?? c.paidWith}
              {c.paidAt &&
                `, ${new Date(c.paidAt).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
              {c.sumupTransactionCode && (
                <span className="tenue piccolo"> · SumUp {c.sumupTransactionCode}</span>
              )}
            </dd>
          </>
        )}
        {c.status === 'pending' && c.holdUntil && (
          <>
            <dt>{fisso ? 'Da versare entro' : 'Il campo è tenuto'}</dt>
            <dd>
              {fisso
                ? `${new Date(c.holdUntil).toLocaleString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}, poi le partite si liberano`
                : `fino alle ${new Date(c.holdUntil).toLocaleTimeString('it-IT').slice(0, 5)}, poi torna libero`}
            </dd>
          </>
        )}
        {!fisso && (c.status === 'pending' || c.status === 'paid') && Date.parse(p.startsAt) > Date.now() && (
          <>
            <dt>Se si disdice</dt>
            <dd>
              {c.refundableNow
                ? `in tempo (fino a ${giornoBreve(c.refundableUntilDate)} alle ${c.refundableUntilTime}): torna nel borsellino come Crediti`
                : 'adesso: resta al circolo'}
            </dd>
            <dt>Campo vuoto</dt>
            <dd>resta al circolo</dd>
          </>
        )}
        {c.closeReason && (
          <>
            <dt>Chiuso</dt>
            <dd>
              {perche(c.closeReason)}
              {c.closedByEmail && <span className="tenue piccolo"> · da {c.closedByEmail}</span>}
            </dd>
          </>
        )}
        {c.appliedAt && (
          <>
            <dt>In cassa</dt>
            <dd>scalato dalle quote dei giocatori</dd>
          </>
        )}
      </dl>

      {sulLettore && (
        <div className="finestra-caparra">
          <p>
            📟 <strong>Sul lettore: {formatCents(c.amountCents - c.creditCents)}</strong> · in attesa della
            carta…
          </p>
          <div className="azioni-caparra">
            <button
              type="button"
              className="bordato"
              disabled={ferma.isPending}
              onClick={() => ferma.mutate()}
            >
              Annulla sul lettore
            </button>
          </div>
          <Guasto errore={ferma.error} />
        </div>
      )}
      {!sulLettore && esitoLettore && esitoLettore !== 'pagata' && inAttesa && (
        <p className="avvertenza">
          📟{' '}
          {esitoLettore === 'rifiutata'
            ? 'Il lettore non ha accettato la carta.'
            : 'Pagamento annullato sul lettore.'}{' '}
          Puoi riprovare, o incassarlo in contanti.
        </p>
      )}

      {c.status === 'paid' && !c.appliedAt && !fisso && (
        <p className="avvertenza">
          <strong>
            💳 Acconto {formatCents(c.amountCents)} già versato da {c.memberName}: da scalare.
          </strong>{' '}
          Prima la sua quota, il resto in parti uguali dagli altri.
        </p>
      )}

      <div className="azioni-caparra">
        {puoModificare && inAttesa && !sulLettore && (
          <button type="button" onClick={() => setApre('banco')}>
            💶 Incassata qui
          </button>
        )}
        {puoIncassare && c.status === 'paid' && !c.appliedAt && !fisso && (
          <button type="button" disabled={gesto.isPending} onClick={() => gesto.mutate('scala')}>
            💶 Scala l’acconto
          </button>
        )}
        {puoModificare && viva && !c.started && (c.status === 'paid' || c.status === 'pending') && (
          <button type="button" className="bordato" onClick={() => setApre('telefono')}>
            📞 Disdetta per telefono
          </button>
        )}
        {puoIncassare &&
          c.status === 'paid' &&
          (fisso ? Date.parse(p.startsAt) <= Date.now() : c.started) &&
          !c.appliedAt && (
            <button type="button" className="bordato" onClick={() => setApre('noshow')}>
              🚫 Segna no-show
            </button>
          )}
        {puoIncassare && !fisso && c.status === 'forfeited' && c.closeReason === 'no_show' && (
          <button type="button" className="bordato" onClick={() => setApre('annulla')}>
            ↩️ Annulla il no-show
          </button>
        )}
        {puoIncassare && c.status === 'forfeited' && (
          <button type="button" className="bordato" onClick={() => setApre('credito')}>
            💶 Accredita a discrezione
          </button>
        )}
      </div>

      {apre === 'banco' && (
        <div className="finestra-caparra">
          <strong>💶 Incassata qui</strong>
          <p>
            {formatCents(c.amountCents - c.creditCents)} da {c.memberName}. Il link nel bot si ritira da solo:
            non potrà pagarlo due volte.
          </p>
          <div className="azioni-caparra">
            <button type="button" disabled={banco.isPending} onClick={() => banco.mutate('cash')}>
              Contanti
            </button>
            {lettore.data?.ready ? (
              <button
                type="button"
                disabled={manda.isPending}
                onClick={() => {
                  setApre(null);
                  setEsitoLettore(null);
                  manda.mutate();
                }}
              >
                📟 Manda al lettore
              </button>
            ) : null}
            <button
              type="button"
              className="bordato"
              disabled={banco.isPending}
              onClick={() => banco.mutate('card')}
            >
              Carta, segnata a mano
            </button>
            <button type="button" className="bordato" onClick={() => setApre(null)}>
              Lascia stare
            </button>
          </div>
          {!lettore.data?.ready && lettore.data?.missing && (
            <p className="tenue piccolo">📟 Lettore Solo: {lettore.data.missing}</p>
          )}
          <Guasto errore={banco.error ?? manda.error} />
        </div>
      )}

      {apre === 'telefono' && (
        <div className="finestra-caparra">
          <strong>📞 Disdetta per telefono o di persona</strong>
          <p>Vale adesso, e si segna chi la registra.</p>
          <p>
            {oreFino > 72 ? `Mancano ${Math.floor(oreFino / 24)} giorni` : `Mancano circa ${oreFino} ore`}:{' '}
            <strong>
              {fisso
                ? Date.parse(p.startsAt) - Date.now() > 48 * 3_600_000
                  ? 'l’acconto del campo fisso resta, per le altre partite'
                  : `mancano meno di 48 ore: l’acconto del campo fisso resta al circolo, e a ${c.memberName} parte il link per versarne uno nuovo`
                : c.status === 'pending'
                  ? 'l’acconto non era ancora pagato, il campo torna libero'
                  : c.refundableNow
                    ? `l’acconto di ${formatCents(c.amountCents)} va nel borsellino di ${c.memberName} come Crediti`
                    : `mancano meno di 48 ore: l’acconto di ${formatCents(c.amountCents)} resta al circolo, e ${c.memberName} riceve nel bot un messaggio cortese, con le strade per la prossima volta`}
            </strong>
            .
          </p>
          <Guasto errore={gesto.error} />
          <div className="azioni-caparra">
            <button type="button" disabled={gesto.isPending} onClick={() => gesto.mutate('telefono')}>
              Registra la disdetta
            </button>
            <button type="button" className="bordato" onClick={() => setApre(null)}>
              Lascia stare
            </button>
          </div>
        </div>
      )}

      {apre === 'noshow' && (
        <div className="finestra-caparra">
          <strong>🚫 Non si sono presentati</strong>
          <p>
            {fisso
              ? `L’acconto del campo fisso (${formatCents(c.amountCents)}) resta al circolo, e a ${c.memberName} parte il link per versarne uno nuovo: se non arriva entro 48 ore dalla partita dopo, le partite successive si liberano.`
              : `L’acconto di ${formatCents(c.amountCents)} resta al circolo, l’assenza si segna su ${c.memberName}, e nel bot riceve un messaggio cortese, con le strade per la prossima volta.`}{' '}
            Se vuoi restituirlo lo stesso, dopo c’è «💶 Accredita a discrezione».
          </p>
          <Guasto errore={gesto.error} />
          <div className="azioni-caparra">
            <button type="button" disabled={gesto.isPending} onClick={() => gesto.mutate('noshow')}>
              Segna no-show
            </button>
            <button type="button" className="bordato" onClick={() => setApre(null)}>
              Lascia stare
            </button>
          </div>
        </div>
      )}

      {apre === 'annulla' && (
        <div className="finestra-caparra">
          <strong>↩️ Annulla il no-show</strong>
          <p>
            Erano in campo: l’acconto di {formatCents(c.amountCents)} torna da scalare, e si toglie dal totale
            della partita come per ogni partita giocata. Nessun soldo esce dal circolo, e {c.memberName}{' '}
            riceve due righe di scuse nel bot.
          </p>
          <label className="campo">
            <span>Perché lo annulli (resta scritto)</span>
            <input
              value={motivo}
              placeholder="per esempio: erano in campo, segnata per sbaglio"
              onChange={(ev) => setMotivo(ev.target.value)}
            />
          </label>
          <Guasto errore={gesto.error} />
          <div className="azioni-caparra">
            <button
              type="button"
              disabled={gesto.isPending || motivo.trim().length < 3}
              onClick={() => gesto.mutate('annulla')}
            >
              Annulla il no-show
            </button>
            <button type="button" className="bordato" onClick={() => setApre(null)}>
              Lascia stare
            </button>
          </div>
        </div>
      )}

      {apre === 'credito' && (
        <div className="finestra-caparra">
          <strong>💶 Accredita a discrezione</strong>
          <p className="piccolo">Per i casi gravi (infortunio, malattia). Il motivo resta scritto.</p>
          <label className="campo">
            <span>Motivo (obbligatorio)</span>
            <textarea rows={2} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} />
          </label>
          <Guasto errore={gesto.error} />
          <div className="azioni-caparra">
            <button
              type="button"
              disabled={gesto.isPending || motivo.trim().length < 3}
              onClick={() => gesto.mutate('credito')}
            >
              Metti {formatCents(c.amountCents)} nel borsellino
            </button>
            <button type="button" className="bordato" onClick={() => setApre(null)}>
              Lascia stare
            </button>
          </div>
        </div>
      )}
      {apre === null && <Guasto errore={gesto.error} />}
    </div>
  );
}

/**
 * 🌙 «Chiedi la caparra del campo fisso» (fase 3, R7): un fisso serale nato
 * prima, senza caparra. Le stesse strade di una prenotazione nuova.
 */
function ChiediLaCaparraDelFisso({
  p,
  puoModificare,
  rileggi,
}: {
  p: SchedaPrenotazione;
  puoModificare: boolean;
  rileggi(): void;
}): React.JSX.Element | null {
  const [apre, setApre] = useState(false);
  const [modo, setModo] = useState<'link' | 'cash' | 'card' | 'esente'>('link');
  const [motivo, setMotivo] = useState('');
  const chiedi = useMutation({
    meta: {
      fatto: (_d, s) => {
        const x = s as CaparraDelFissoScelta;
        return x.modo === 'link'
          ? 'link dell’acconto del campo fisso mandato'
          : x.modo === 'esente'
            ? 'campo fisso segnato: l’acconto non serve'
            : 'acconto del campo fisso incassato';
      },
    },
    mutationFn: (s: CaparraDelFissoScelta) => chiama('POST', `/series/${p.serie!.id}/deposit`, s),
    onSuccess: () => {
      setApre(false);
      rileggi();
    },
  });
  if (!puoModificare || !p.serie) return null;
  if (!apre) {
    return (
      <div className="azioni-caparra">
        <button type="button" className="bordato" onClick={() => setApre(true)}>
          🌙 Chiedi l’acconto del campo fisso
        </button>
      </div>
    );
  }
  return (
    <div className="finestra-caparra">
      <strong>🌙 L’acconto del campo fisso</strong>
      <p className="piccolo">
        Uno solo per tutte le partite che restano: si scala dall’ultima. Se una sera il campo resta vuoto, o
        la partita si disdice a meno di 48 ore, resta al circolo e ne serve uno nuovo.
      </p>
      <div className="scelte-caparra">
        {(
          [
            ['link', 'Mando il link al socio nel bot'],
            ['cash', 'L’ho incassato qui, in contanti'],
            ['card', 'L’ho incassato qui, con la carta'],
            ['esente', 'Non serve · un caso particolare'],
          ] as const
        ).map(([v, t]) => (
          <label key={v}>
            <input type="radio" checked={modo === v} onChange={() => setModo(v)} />
            <span>
              <strong>{t}</strong>
            </span>
          </label>
        ))}
      </div>
      {modo === 'esente' && (
        <label className="campo">
          <span>Perché non serve (resta scritto nei resoconti)</span>
          <input value={motivo} onChange={(ev) => setMotivo(ev.target.value)} />
        </label>
      )}
      <Guasto errore={chiedi.error} />
      <div className="azioni-caparra">
        <button
          type="button"
          disabled={chiedi.isPending || (modo === 'esente' && motivo.trim().length < 3)}
          onClick={() =>
            chiedi.mutate(
              modo === 'link'
                ? { modo: 'link' }
                : modo === 'esente'
                  ? { modo: 'esente', motivo: motivo.trim() }
                  : { modo: 'banco', metodo: modo },
            )
          }
        >
          Conferma
        </button>
        <button type="button" className="bordato" onClick={() => setApre(false)}>
          Lascia stare
        </button>
      </div>
    </div>
  );
}
